import { useState, useEffect } from 'react';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import CommonConfig from '../../utils/constant';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { useCatchResponse } from '../../utils/useCatchResponse';
import { apiBase } from '../../utils/config';

const AttendanceReport = () => {
    const { catchResponse } = useCatchResponse();
    const loginData = CommonConfig.loginData();
    const [classList, setClassList] = useState([]);
    const [subjectList, setSubjectList] = useState([]);
    const [selectedClass, setSelectedClass] = useState(null);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [endDate, setEndDate] = useState(new Date());
    const [reportRows, setReportRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [selectedYear, setSelectedYear] = useState('');
    const [rows, setRows] = useState(10);
    const [first, setFirst] = useState(0);
    const [reportType, setReportType] = useState('day');
    const [viewMode, setViewMode] = useState('student');
    const [facultyRows, setFacultyRows] = useState([]);
    const [staffRows, setStaffRows] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');

    useEffect(() => {
        init();
    }, []);

    useEffect(() => {
        if (viewMode === 'faculty' && selectedYear) loadFacultyReport();
    }, [viewMode, selectedYear, selectedClass]);

    const scopeId = () => CommonConfig.isOrgOwner(loginData?.UserRole)
        ? loginData?._id : (loginData?.CreatedBy || loginData?._id);

    const init = async () => {
        const years = await CommonConfig.getFinancialYears(loginData?.CreatedBy || loginData?._id);
        const currentYear = CommonConfig.getCurrentFinancialYear(years);
        const cookieYear = CommonConfig.getCookie('selectedAcademicYear');
        const fy = cookieYear ?? currentYear?.value;
        setSelectedYear(fy);
        await GetClassList(fy);
    };

    const GetClassList = async (fy) => {
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.getClassList, { UserID: scopeId(), FinancialYearId: fy });
            if (res.success) {
                setClassList((res?.data || []).filter(x => x?.Status === 'Active').map(x => ({
                    value: x?._id,
                    label: `${x?.ClassName}-${x?.SectionFullName}-${x?.MediumName}-${x?.Board}`,
                })));
            }
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const GetSubjectList = async (classId) => {
        try {
            const res = await api.post(APIConstant.path.getSubjectByClassId, { ClassId: classId, UserID: scopeId() });
            if (res.success) {
                setSubjectList((res?.data || []).map(x => ({ value: x?._id, label: x?.SubjectName })));
            } else {
                setSubjectList([]);
            }
        } catch (err) { setSubjectList([]); }
    };

    const handleClassChange = (value) => {
        setSelectedClass(value);
        setSelectedSubject(null);
        setReportRows([]);
        setSummary(null);
        if (value) GetSubjectList(value);
    };

    const toYMD = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}/${m}/${day}`;
    };

    const buildParams = (isExport) => ({
        ClassId: selectedClass,
        SubjectId: selectedSubject || undefined,
        StartDate: toYMD(startDate),
        EndDate: toYMD(endDate),
        FinancialYearId: selectedYear,
        SchoolId: scopeId(),
        Mode: reportType,
        ...(isExport ? { Export: 'excel' } : {}),
    });

    const loadStaffReport = async () => {
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.GetStaffAttendanceReport, {
                SchoolId: scopeId(),
                FinancialYearId: selectedYear,
                FromDate: toYMD(startDate),
                ToDate: toYMD(endDate),
            });
            if (res.success) {
                setStaffRows(res?.data || []);
                setFirst(0);
            }
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const buildFacultyParams = () => ({
        UserID: loginData?._id,
        SchoolId: scopeId(),
        FinancialYearId: selectedYear,
        ClassId: selectedClass || undefined,
    });

    const loadFacultyReport = async () => {
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.GetFacultyLectureReport, buildFacultyParams());
            if (res.success) {
                setFacultyRows(res?.data || []);
                setFirst(0);
            }
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const handleStaffExport = async () => {
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.ExportStaffAttendanceReport, {
                SchoolId: scopeId(),
                FinancialYearId: selectedYear,
                FromDate: toYMD(startDate),
                ToDate: toYMD(endDate),
            });
            if (res.success) {
                if (res?.fileUrl) downloadFile(res.fileUrl);
            }
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const handleFacultyExport = async () => {
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.ExportFacultyLectureReport, buildFacultyParams());
            if (res.success) {
                if (res?.fileUrl) downloadFile(res.fileUrl);
            }
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const downloadFile = (fileUrl) => {
        const url = `${apiBase}${fileUrl}?token=${CommonConfig.fileToken()}`;
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileUrl.split('/').pop());
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const validate = () => {
        if (!selectedClass) { catchResponse({ message: 'Please select a class' }); return false; }
        if (!startDate || !endDate) { catchResponse({ message: 'Please select a date range' }); return false; }
        if (startDate > endDate) { catchResponse({ message: 'Start date cannot be after end date' }); return false; }
        return true;
    };

    const handleView = async () => {
        if (!validate()) return;
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.GetAttendanceReport, buildParams(false));
            if (res.success) {
                setReportRows(res?.data?.rows || []);
                setSummary(res?.data?.summary || null);
                setFirst(0);
            }
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const handleDownload = async () => {
        if (!validate()) return;
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.GetAttendanceReport, buildParams(true));
            if (res.success) {
                setReportRows(res?.data?.rows || []);
                setSummary(res?.data?.summary || null);
                if (res?.fileUrl) downloadFile(res.fileUrl);
            }
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const srNoTemplate = (data, props) => props.rowIndex + 1;
    const pctTemplate = (row) => (
        <span style={{ fontWeight: 'bold', color: row?.Percentage >= 75 ? 'green' : row?.Percentage >= 50 ? '#d08700' : 'red' }}>
            {row?.Percentage}%
        </span>
    );

    return (
        <div className='wayment-owner-dashboard et-master-page'>
            <div className="heading-area p-d-flex p-ai-center p-jc-between">
                <div className="p-d-flex p-ai-baseline">
                    <i className="pi pi-chart-bar"></i>
                    <h2>Attendance Report</h2>
                </div>
            </div>

            <div className="p-d-flex p-ai-center p-mb-3" style={{ gap: '8px', padding: '0 8px' }}>
                <Button label="Student Wise" className={viewMode === 'student' ? 'btn-text-action-primary' : 'btn-text-action-secondary'}
                    onClick={() => { setViewMode('student'); setFirst(0); }} />
                <Button label="Faculty Wise" className={viewMode === 'faculty' ? 'btn-text-action-primary' : 'btn-text-action-secondary'}
                    onClick={() => { setViewMode('faculty'); setFirst(0); }} />
                <Button label="Staff Wise" className={viewMode === 'staff' ? 'btn-text-action-primary' : 'btn-text-action-secondary'}
                    onClick={() => { setViewMode('staff'); setFirst(0); }} />
            </div>

            <div className="p-grid p-ai-end p-mb-3" style={{ gap: '10px', padding: '0 8px' }}>
                {viewMode !== 'staff' && <div>
                    <p className="p-mb-1">Class</p>
                    <Dropdown value={selectedClass} options={classList} onChange={(e) => handleClassChange(e.value)}
                        placeholder="Select Class" filter showClear style={{ minWidth: '220px', height: '36px' }} />
                </div>}
                {viewMode === 'student' && (
                <div>
                    <p className="p-mb-1">Report Type</p>
                    <Dropdown value={reportType} options={[{ label: 'Day-wise', value: 'day' }, { label: 'Lecture-wise', value: 'lecture' }]}
                        onChange={(e) => { setReportType(e.value); if (e.value !== 'lecture') setSelectedSubject(null); }} style={{ minWidth: '150px', height: '36px' }} />
                </div>
                )}
                {viewMode === 'student' && reportType === 'lecture' && (
                    <div>
                        <p className="p-mb-1">Subject (optional)</p>
                        <Dropdown value={selectedSubject} options={subjectList} onChange={(e) => setSelectedSubject(e.value)}
                            placeholder="All subjects" filter showClear style={{ minWidth: '180px', height: '36px' }} />
                    </div>
                )}
                {viewMode !== 'faculty' && (
                <div>
                    <p className="p-mb-1">From</p>
                    <Calendar value={startDate} onChange={(e) => setStartDate(e.value)} dateFormat="dd/mm/yy" style={{ height: '36px' }} />
                </div>
                )}
                {viewMode !== 'faculty' && (
                <div>
                    <p className="p-mb-1">To</p>
                    <Calendar value={endDate} onChange={(e) => setEndDate(e.value)} dateFormat="dd/mm/yy" style={{ height: '36px' }} />
                </div>
                )}
                <div className="p-d-flex" style={{ gap: '8px' }}>
                    {viewMode === 'staff' ? (
                        <>
                            <Button label="View" icon="pi pi-search" className="btn-text-action-primary" onClick={loadStaffReport} />
                            <Button label="Export Excel" icon="pi pi-file-excel" className="btn-text-action-secondary" onClick={handleStaffExport} />
                        </>
                    ) : viewMode === 'student' ? (
                        <>
                            <Button label="View" icon="pi pi-search" className="btn-text-action-primary" onClick={handleView} />
                            <Button label="Download Excel" icon="pi pi-file-excel" className="btn-text-action-secondary" onClick={handleDownload} />
                        </>
                    ) : (
                        <Button label="Export Excel" icon="pi pi-file-excel" className="btn-text-action-secondary" onClick={handleFacultyExport} />
                    )}
                </div>
            </div>

            {viewMode === 'student' && summary && (
                <div className="p-mb-2" style={{ padding: '0 8px', fontSize: '0.9rem' }}>
                    <strong>{summary?.className}</strong>
                    {summary?.subjectName ? ` — ${summary.subjectName}` : ''}
                    {`  |  ${summary?.mode === 'lecture' ? 'Lecture-wise' : 'Day-wise'}  |  ${summary?.rangeStart} to ${summary?.rangeEnd}  |  Students: ${summary?.totalStudents}  |  Avg: ${summary?.avgPercentage}%`}
                </div>
            )}

            {viewMode === 'faculty' && (
                <div className="searchbar-area e-searchbar-area" style={{ margin: '0 8px 8px' }}>
                    <i className="pi pi-search" />
                    <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Global Search" />
                </div>
            )}

            {viewMode === 'faculty' && (
            <DataTable
                value={facultyRows}
                paginator rows={rows} first={first}
                onPage={(e) => { setFirst(e.first); setRows(e.rows); }}
                rowsPerPageOptions={[10, 20, 50]}
                paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                currentPageReportTemplate="Showing {first}-{last} of {totalRecords} records"
                responsiveLayout="scroll" className='custom-table' scrollHeight='calc(100vh - 300px)' size='small'
                globalFilter={globalFilter} globalFilterFields={['TeacherName', 'SubjectName', 'ClassName']}
                emptyMessage="No data. Select filters.">
                <Column body={srNoTemplate} header="Sr No" />
                <Column field="TeacherName" header="Faculty" />
                <Column field="SubjectName" header="Subject" />
                <Column field="ClassName" header="Class" />
                <Column field="Lectures" header="Lectures" />
                <Column field="ProxyLectures" header="Proxy Lectures" />
                <Column field="GivenToProxy" header="Given To Proxy" />
                <Column field="ProxyDates" header="Proxy Dates" body={(r) => (Array.isArray(r?.ProxyDates) && r.ProxyDates.length ? r.ProxyDates.join(', ') : '--')} />
                <Column field="ProxyClassTimes" header="Proxy Class Time" body={(r) => (Array.isArray(r?.ProxyClassTimes) && r.ProxyClassTimes.length ? r.ProxyClassTimes.join(', ') : '--')} />
            </DataTable>
            )}

            {viewMode === 'staff' && (
            <DataTable
                value={staffRows}
                paginator rows={rows} first={first}
                onPage={(e) => { setFirst(e.first); setRows(e.rows); }}
                rowsPerPageOptions={[10, 20, 50]}
                paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                currentPageReportTemplate="Showing {first}-{last} of {totalRecords} teachers"
                responsiveLayout="scroll" className='custom-table' scrollHeight='calc(100vh - 300px)' size='small'
                emptyMessage="No data. Select date range and click View.">
                <Column body={srNoTemplate} header="Sr No" />
                <Column field="TeacherName" header="Teacher Name" />
                <Column field="Present" header="Present" />
                <Column field="Absent" header="Absent" />
                <Column field="Late" header="Late" />
                <Column field="Holidays" header="Holidays" />
                <Column field="Total" header="Total" />
            </DataTable>
            )}

            {viewMode === 'student' && <DataTable
                value={reportRows}
                paginator rows={rows} first={first}
                onPage={(e) => { setFirst(e.first); setRows(e.rows); }}
                rowsPerPageOptions={[10, 20, 50]}
                paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                currentPageReportTemplate="Showing {first}-{last} of {totalRecords} records"
                responsiveLayout="scroll" className='custom-table' scrollHeight='calc(100vh - 300px)' size='small'
                emptyMessage="No data. Select filters and click View.">
                <Column body={srNoTemplate} header="Sr No." />
                <Column field="RollNo" header="Roll No." />
                <Column field="StudentName" header="Student Name" />
                <Column field="Present" header="Present" />
                <Column field="Absent" header="Absent" />
                <Column field="Late" header="Late" />
                {summary?.mode !== 'lecture' && <Column field="Partial" header="Partial" />}
                {summary?.mode === 'lecture'
                    ? <Column field="TotalLectures" header="Total Lectures" />
                    : <Column field="WorkingDays" header="Working Days" />}
                <Column body={pctTemplate} header="Percentage" />
            </DataTable>}
        </div>
    );
};

export default AttendanceReport;
